// Shared admin date-range picker — Shopify-style trigger + popover used by the
// Dashboard and the Comptabilité cockpit. Replaces the old preset pills.
//
// Quick presets (Aujourd'hui … Maximum) apply on a single tap; a dual-month
// calendar (single on mobile) lets you pick an arbitrary start→end range, then
// Appliquer commits it. Output is the same `{ from, to }` ISO shape the existing
// /accounting/summary, /accounting/expenses and /orders/stats/dashboard
// endpoints already consume — so AMEEX + all-status windowing keeps working with
// no backend change.
//
// Premium cream/gold: brand tokens, portal popover with blurred backdrop on
// desktop, bottom-sheet on mobile, ≥44px touch targets, respects .adm-rise.

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ADM_COLORS as COLORS } from '@/lib/brand';
import { useT } from '@/lib/i18n';

export type DateRange = { from?: string; to?: string };

const HEADING = "'Fraunces', 'Cormorant Garamond', Georgia, serif";

const pad = (n: number) => String(n).padStart(2, '0');
const isoDay = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const startIso = (d: Date) => `${isoDay(d)}T00:00:00Z`;
const endIso = (d: Date) => `${isoDay(d)}T23:59:59Z`;
const sameDay = (a: Date, b: Date) => isoDay(a) === isoDay(b);
const dayFromIso = (s?: string): Date | null => {
  if (!s) return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

// ─── Presets ──────────────────────────────────────────────────────────────────
export type PresetKey =
  | 'today' | 'yesterday' | '7d' | '14d' | '30d' | 'thisMonth' | 'lastMonth' | 'all';

const PRESET_ORDER: PresetKey[] = ['today', 'yesterday', '7d', '14d', '30d', 'thisMonth', 'lastMonth', 'all'];
const PRESET_LABEL: Record<PresetKey, string> = {
  today: "Aujourd'hui",
  yesterday: 'Hier',
  '7d': '7 derniers jours',
  '14d': '14 derniers jours',
  '30d': '30 derniers jours',
  thisMonth: 'Ce mois-ci',
  lastMonth: 'Mois dernier',
  all: 'Maximum',
};

export function presetRange(key: PresetKey): DateRange {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const back = (n: number) => { const d = new Date(today); d.setDate(d.getDate() - n); return d; };
  switch (key) {
    case 'today': return { from: startIso(today), to: endIso(today) };
    case 'yesterday': { const d = back(1); return { from: startIso(d), to: endIso(d) }; }
    case '7d': return { from: startIso(back(6)), to: endIso(today) };
    case '14d': return { from: startIso(back(13)), to: endIso(today) };
    case '30d': return { from: startIso(back(29)), to: endIso(today) };
    case 'thisMonth': return { from: startIso(new Date(now.getFullYear(), now.getMonth(), 1)), to: endIso(today) };
    case 'lastMonth': {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: startIso(first), to: endIso(last) };
    }
    case 'all': default: return {};
  }
}

function matchPreset(value: DateRange): PresetKey | null {
  for (const k of PRESET_ORDER) {
    const r = presetRange(k);
    if ((r.from ?? '') === (value.from ?? '') && (r.to ?? '') === (value.to ?? '')) return k;
  }
  return null;
}

// ─── Component ──────────────────────────────────────────────────────────────────
export function DateRangePicker({
  value,
  onChange,
  align = 'left',
}: {
  value: DateRange;
  onChange: (v: DateRange) => void;
  align?: 'left' | 'right';
}) {
  const { t, lang } = useT();
  const locale = lang === 'en' ? 'en-GB' : 'fr-FR';
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Draft calendar selection (committed only on Appliquer for custom ranges).
  const [start, setStart] = useState<Date | null>(dayFromIso(value.from));
  const [end, setEnd] = useState<Date | null>(dayFromIso(value.to));
  const [viewMonth, setViewMonth] = useState<Date>(() => {
    const base = dayFromIso(value.to) ?? dayFromIso(value.from) ?? new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  const PANEL_W = 620;

  function openPicker() {
    setStart(dayFromIso(value.from));
    setEnd(dayFromIso(value.to));
    const base = dayFromIso(value.to) ?? dayFromIso(value.from) ?? new Date();
    setViewMonth(new Date(base.getFullYear(), base.getMonth(), 1));
    const mobile = window.innerWidth < 720;
    setIsMobile(mobile);
    if (!mobile && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      const left = align === 'right'
        ? Math.max(8, r.right - PANEL_W)
        : Math.min(r.left, window.innerWidth - PANEL_W - 8);
      setPos({ top: r.bottom + 8, left: Math.max(8, left) });
    }
    setOpen(true);
  }

  // Close on Escape; close (cancel) on scroll/resize so the popover never floats away.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onScroll = () => setOpen(false);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  function pickPreset(k: PresetKey) {
    onChange(presetRange(k)); // presets apply instantly
    setOpen(false);
  }
  function pickDay(d: Date) {
    if (!start || (start && end)) { setStart(d); setEnd(null); return; }
    if (d < start) { setStart(d); setEnd(null); }
    else setEnd(d);
  }
  function apply() {
    if (start && end) onChange({ from: startIso(start), to: endIso(end) });
    else if (start) onChange({ from: startIso(start), to: endIso(start) });
    setOpen(false);
  }

  // ── Trigger label ──
  const fmtShort = (d: Date) => d.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  const triggerLabel = (() => {
    const p = matchPreset(value);
    if (p) return t(PRESET_LABEL[p]);
    const f = dayFromIso(value.from), tt = dayFromIso(value.to);
    if (!f && !tt) return t('Maximum');
    if (f && tt) return `${fmtShort(f)} – ${fmtShort(tt)}`;
    if (f) return `${t('Depuis')} ${fmtShort(f)}`;
    return `${t("Jusqu'au")} ${fmtShort(tt as Date)}`;
  })();

  const activePreset = matchPreset(value);
  const draftLabel = start && end
    ? `${fmtShort(start)} – ${fmtShort(end)}`
    : start
      ? `${fmtShort(start)} → …`
      : t('Sélectionnez une période');

  // ── Calendar month grid ──
  const weekdays = lang === 'en'
    ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    : ['lun', 'mar', 'mer', 'jeu', 'ven', 'sam', 'dim'];

  function MonthGrid({ month }: { month: Date }) {
    const year = month.getFullYear(), m = month.getMonth();
    const first = new Date(year, m, 1);
    const lead = (first.getDay() + 6) % 7; // Monday-first
    const daysInMonth = new Date(year, m + 1, 0).getDate();
    const today = new Date();
    const cells: (Date | null)[] = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, m, d));
    while (cells.length % 7 !== 0) cells.push(null);

    return (
      <div className="flex-1 min-w-0">
        <p className="text-center text-[13px] font-semibold mb-2 capitalize" style={{ color: COLORS.textPrimary, fontFamily: HEADING }}>
          {month.toLocaleDateString(locale, { month: 'long', year: 'numeric' })}
        </p>
        <div className="grid grid-cols-7 mb-1">
          {weekdays.map((w) => (
            <div key={w} className="text-center text-[10px] font-semibold uppercase tracking-wide py-1" style={{ color: COLORS.textTertiary }}>{w}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((d, i) => {
            if (!d) return <div key={i} className="h-9" />;
            const inRange = !!(start && end && d >= start && d <= end);
            const isStart = !!(start && sameDay(d, start));
            const isEnd = !!(end && sameDay(d, end));
            const endpoint = isStart || isEnd;
            const isToday = sameDay(d, today);
            const future = d > today;
            const cellBg = inRange && !endpoint ? COLORS.accentSoft : 'transparent';
            const round = endpoint
              ? (isStart && isEnd) ? '9999px' : isStart ? '9999px 0 0 9999px' : '0 9999px 9999px 0'
              : inRange ? '0' : '9999px';
            return (
              <div key={i} className="h-9 flex items-center justify-center"
                style={{ background: inRange ? (endpoint ? COLORS.accentSoft : cellBg) : 'transparent', borderRadius: inRange ? round : undefined }}>
                <button
                  type="button"
                  disabled={future}
                  onClick={() => pickDay(d)}
                  className="w-9 h-9 rounded-full text-[13px] flex items-center justify-center transition-colors"
                  style={{
                    background: endpoint ? COLORS.accentPrimary : 'transparent',
                    color: endpoint ? '#FFF' : future ? COLORS.textTertiary : COLORS.textPrimary,
                    fontWeight: endpoint || isToday ? 700 : 400,
                    opacity: future ? 0.4 : 1,
                    cursor: future ? 'not-allowed' : 'pointer',
                    boxShadow: isToday && !endpoint ? `inset 0 0 0 1.5px ${COLORS.accentPrimary}` : undefined,
                  }}
                >
                  {d.getDate()}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const nextMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1);

  // ── Presets rail / chips ──
  const PresetList = ({ horizontal }: { horizontal?: boolean }) => (
    <div className={horizontal ? 'flex gap-2 overflow-x-auto pb-1' : 'flex flex-col gap-0.5'}>
      {PRESET_ORDER.map((k) => {
        const on = activePreset === k;
        return (
          <button key={k} type="button" onClick={() => pickPreset(k)}
            className={`text-left rounded-lg text-[13px] font-medium transition-colors whitespace-nowrap ${horizontal ? 'px-3 py-2 border' : 'px-3 py-2'}`}
            style={on
              ? { background: COLORS.accentSoft, color: COLORS.accentHover, borderColor: COLORS.accentPrimary }
              : { background: 'transparent', color: COLORS.textSecondary, borderColor: COLORS.borderSubtle }}>
            {t(PRESET_LABEL[k])}
          </button>
        );
      })}
    </div>
  );

  const calendarNav = (
    <div className="flex items-center justify-between mb-1">
      <button type="button" onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))}
        className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ color: COLORS.textSecondary }} aria-label={t('Mois précédent')}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
      </button>
      <button type="button" onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))}
        className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ color: COLORS.textSecondary }} aria-label={t('Mois suivant')}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
      </button>
    </div>
  );

  const footer = (
    <div className="flex items-center gap-3 pt-3 mt-1 border-t" style={{ borderColor: COLORS.borderSubtle }}>
      <div className="min-w-0">
        <p className="text-[12px] font-medium truncate" style={{ color: COLORS.textPrimary }}>{draftLabel}</p>
        <p className="text-[10px]" style={{ color: COLORS.textTertiary }}>{t('Heure de Casablanca')}</p>
      </div>
      <div className="ml-auto flex gap-2">
        <button type="button" onClick={() => setOpen(false)}
          className="px-3.5 py-2 rounded-lg text-[13px] font-semibold border" style={{ borderColor: COLORS.borderSubtle, color: COLORS.textSecondary, background: COLORS.bgCard }}>
          {t('Annuler')}
        </button>
        <button type="button" onClick={apply} disabled={!start}
          className="px-4 py-2 rounded-lg text-[13px] font-semibold" style={{ background: start ? COLORS.accentPrimary : COLORS.borderSubtle, color: '#FFF', opacity: start ? 1 : 0.6 }}>
          {t('Appliquer')}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={openPicker}
        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-[13px] md:text-sm font-medium border transition-colors min-h-[44px]"
        style={{ background: COLORS.bgCard, borderColor: open ? COLORS.accentPrimary : COLORS.borderSubtle, color: COLORS.textPrimary }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={COLORS.accentPrimary} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <span className="truncate max-w-[200px]">{triggerLabel}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={COLORS.textTertiary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[90]" onClick={() => setOpen(false)}
          style={{ background: isMobile ? 'rgba(17,17,17,0.18)' : 'transparent', backdropFilter: isMobile ? 'blur(6px)' : undefined }}>
          {isMobile ? (
            // Bottom sheet
            <div className="absolute inset-x-0 bottom-0 rounded-t-2xl p-4 shadow-2xl adm-rise max-h-[88vh] overflow-auto"
              style={{ background: COLORS.bgCard }} onClick={(e) => e.stopPropagation()}>
              <div className="w-10 h-1 rounded-full mx-auto mb-3" style={{ background: COLORS.borderSubtle }} />
              <PresetList horizontal />
              <div className="mt-3">{calendarNav}<MonthGrid month={viewMonth} /></div>
              {footer}
            </div>
          ) : (
            // Desktop popover
            <div className="absolute rounded-2xl shadow-2xl adm-rise p-4 border"
              style={{ top: pos?.top ?? 80, left: pos?.left ?? 80, width: PANEL_W, background: COLORS.bgCard, borderColor: COLORS.borderSubtle }}
              onClick={(e) => e.stopPropagation()}>
              <div className="flex gap-4">
                <div className="w-[160px] shrink-0 border-r pr-3" style={{ borderColor: COLORS.borderSubtle }}>
                  <p className="text-[10px] uppercase tracking-wider font-semibold mb-1.5" style={{ color: COLORS.textTertiary }}>{t('Raccourcis')}</p>
                  <PresetList />
                </div>
                <div className="flex-1 min-w-0">
                  {calendarNav}
                  <div className="flex gap-5">
                    <MonthGrid month={viewMonth} />
                    <MonthGrid month={nextMonth} />
                  </div>
                </div>
              </div>
              {footer}
            </div>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}
